import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

import type { LegalEntity } from '@/features/legal-entities/data/legal-entity';
import { useMyLegalEntities } from '@/features/legal-entities/data/useMyLegalEntities';

const STORAGE_KEY = 'activeEntityId';

interface LegalEntityContextValue {
  entities: LegalEntity[];
  activeEntity: LegalEntity | null;
  setActiveEntity: (entity: LegalEntity) => void;
  isLoading: boolean;
}

const LegalEntityContext = createContext<LegalEntityContextValue | null>(null);

export function LegalEntityProvider({ children }: { readonly children: ReactNode }) {
  const { data: entities = [], isLoading } = useMyLegalEntities();
  const [activeEntityId, setActiveEntityId] = useState<string | null>(() => localStorage.getItem(STORAGE_KEY));

  const activeEntity = useMemo(() => {
    if (entities.length === 0) return null;
    const found = activeEntityId ? entities.find((e) => e.id === activeEntityId) : null;
    return found ?? entities[0];
  }, [entities, activeEntityId]);

  const setActiveEntity = (entity: LegalEntity) => {
    localStorage.setItem(STORAGE_KEY, entity.id);
    setActiveEntityId(entity.id);
  };

  return <LegalEntityContext.Provider value={{ entities, activeEntity, setActiveEntity, isLoading }}>{children}</LegalEntityContext.Provider>;
}

export function useLegalEntityContext() {
  const ctx = useContext(LegalEntityContext);
  if (!ctx) throw new Error('useLegalEntityContext must be used within LegalEntityProvider');
  return ctx;
}
